/**
 * Formato dos números, em pt-BR. Porte direto do protótipo.
 *
 * REGRA DO PAINEL: tudo em reais, NENHUM número em percentual na tela. A
 * proporção existe só para posicionar a agulha do velocímetro e escolher a cor
 * da faixa — e nunca vira texto. Por isso não há nenhuma função de percentual
 * aqui, e não é esquecimento.
 */

/* `Intl.NumberFormat` é instanciado UMA vez. Criar o formatador a cada
   repintura custa caro, e esta tela repinta a cada pedido que entra, por
   4 dias seguidos. */
const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
const UMA_CASA = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
const HORA = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})
const DATA_CURTA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
})

/** `R$ 83.460.000` — o número cheio, para o herói do topo e para o feed. */
export function moeda(valor: number): string {
  if (!Number.isFinite(valor)) return 'R$ —'
  return 'R$ ' + INTEIRO.format(valor)
}

/** `R$ 6,5 mi` — para card e subitem, onde o número cheio não cabe. */
export function milhoes(valor: number): string {
  if (!Number.isFinite(valor)) return 'R$ —'
  return 'R$ ' + UMA_CASA.format(valor / 1e6) + ' mi'
}

/**
 * O VENDIDO, em MILHARES e com todas as casas até elas.
 *
 * `milhoes()` continua valendo para a META, que é cadastrada, redonda e não
 * muda durante o evento. Para o vendido, qualquer escala mais grossa que o
 * milhar apaga informação justo na hora que mais importa, e isso aconteceu
 * duas vezes em 13/10:
 *
 *   - às 9h do primeiro dia a Alakazoo tinha R$ 37.385 na tela como `R$ 0,0 mi`,
 *     indistinguível de divisão que não vendeu nada, com pedido entrando na sala;
 *   - à tarde a Lez passou de um milhão e virou `R$ 1,4 mi` — a sala não tinha
 *     como saber se eram 1.401 ou 1.449 mil, uma diferença de R$ 48 mil.
 *
 * Daí a escala ser uma só, sem virar para `mi` em nenhum ponto:
 *
 *   R$ 0            zero é zero, não `R$ 0,0 mil`
 *   R$ 830          abaixo de mil, o valor cheio — não existe `R$ 0,8 mil`
 *   R$ 9 mil        a Vila Flor num dia fraco
 *   R$ 1.478 mil    a Lez depois de passar o milhão
 *   R$ 70.000 mil   a meta da Malhas, se um dia o vendido chegar lá
 *
 * Decidido em 13/10, com o telão no ar: a leitura de longe perde menos com um
 * número de seis dígitos do que com um arredondamento que esconde dezenas de
 * milhares. Negativo é possível (devolução) e o sinal vem na frente do `R$`.
 */
export function valorCurto(valor: number): string {
  if (!Number.isFinite(valor)) return 'R$ —'
  const sinal = valor < 0 ? '-' : ''
  const absoluto = Math.abs(valor)
  if (absoluto < 1_000) return sinal + 'R$ ' + INTEIRO.format(absoluto)
  /* `INTEIRO` arredonda, e é isso que se quer: R$ 1.478.350 vira `R$ 1.478 mil`
     e R$ 999.600 vira `R$ 1.000 mil`. O segundo era um problema quando a escala
     virava para `mi` ("um milhão escrito do jeito errado"); com a escala única
     ele é só o milhar seguinte, e lê-se certo. */
  return sinal + 'R$ ' + INTEIRO.format(absoluto / 1e3) + ' mil'
}

/** `09:42:03`, do relógio do cabeçalho. */
export function hora(quando: Date | number): string {
  return HORA.format(quando)
}

/**
 * `09/10/26`, das duas colunas de data do feed.
 *
 * A entrada é `YYYY-MM-DD` (o contrato do snapshot). Monta-se a data com
 * `new Date(ano, mês-1, dia)` de propósito: `new Date('2026-10-09')` é
 * interpretado como UTC, e no fuso de Brasília isso volta um dia — o pedido de
 * 09/10 apareceria como 08/10 no telão.
 */
export function dataCurta(iso: string | null | undefined): string {
  if (!iso) return '—'
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!partes) return '—'
  const [, ano, mes, dia] = partes
  return DATA_CURTA.format(new Date(Number(ano), Number(mes) - 1, Number(dia)))
}

/**
 * Largura do preenchimento da barra do subitem, em porcentagem do elemento.
 *
 * É geometria de CSS, não número exibido: a escala da barra é a mesma do
 * velocímetro (teto em 120 % da meta), para a barra cheia e a agulha no fim
 * significarem a mesma coisa.
 */
export function larguraBarra(proporcao: number): string {
  const p = Number.isFinite(proporcao) ? Math.max(proporcao, 0) : 0
  return (Math.min(p, 1.2) / 1.2) * 100 + '%'
}
